import { type ChangeEvent, type ClipboardEvent, useCallback, useMemo, useState } from 'react';

type NativeTextInputElement = HTMLInputElement | HTMLTextAreaElement;

interface UseNativeCharacterLimitOptions {
  maxLength: number;
  onValueChange?: (value: string) => void;
  value: string;
}

export function useNativeCharacterLimit({
  maxLength,
  onValueChange,
  value,
}: UseNativeCharacterLimitOptions) {
  const [isFocused, setIsFocused] = useState(false);

  const currentLength = value.length;
  const remainingCharacters = Math.max(0, maxLength - currentLength);

  const onFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const onBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  const onChangeWithLimit = useCallback(
    (event: ChangeEvent<NativeTextInputElement>) => {
      const nextValue = event.target.value.slice(0, maxLength);

      onValueChange?.(nextValue);
    },
    [maxLength, onValueChange],
  );

  const onPasteWithLimit = useCallback(
    (event: ClipboardEvent<NativeTextInputElement>) => {
      event.preventDefault();

      const clipboardValue = event.clipboardData.getData('text');
      const input = event.currentTarget;
      const selectionStart = input.selectionStart ?? input.value.length;
      const selectionEnd = input.selectionEnd ?? input.value.length;
      const selectedTextLength = selectionEnd - selectionStart;
      const availableCharacterBudget = Math.max(0, maxLength - (value.length - selectedTextLength));
      const truncatedClipboardValue = clipboardValue.slice(0, availableCharacterBudget);
      const nextValue = `${value.slice(0, selectionStart)}${truncatedClipboardValue}${value.slice(selectionEnd)}`;

      onValueChange?.(nextValue);
    },
    [maxLength, onValueChange, value],
  );

  return useMemo(
    () => ({
      currentLength,
      isFocused,
      onBlur,
      onChangeWithLimit,
      onFocus,
      onPasteWithLimit,
      remainingCharacters,
    }),
    [
      currentLength,
      isFocused,
      onBlur,
      onChangeWithLimit,
      onFocus,
      onPasteWithLimit,
      remainingCharacters,
    ],
  );
}
